import { useEffect, useMemo, useRef, useState } from 'react';
import MultiSelect, { type Option } from './MultiSelect';
import type { Filters } from '../../../shared/types';

export interface FilterValue {
  categories: string[];
  attributes: string[];
  monsterTypes: string[];
  monsterCategories: string[];
  spellTypes: string[];
  trapTypes: string[];
  skillCharacters: string[];
  sets: string[];
}

export const EMPTY_FILTERS: FilterValue = {
  categories: [], attributes: [], monsterTypes: [], monsterCategories: [],
  spellTypes: [], trapTypes: [], skillCharacters: [], sets: [],
};

const opts = (v: string[]): Option[] => v.map(x => ({ value: x, label: x }));

/**
 * Compact filter control: a "Filters" button that opens a collapsible panel
 * holding all eight card filters (shown contextually by selected category).
 * Closes on outside click. `value` is controlled by the parent.
 */
export default function CardFilterBar({
  filters, value, onChange, onClear,
}: {
  filters: Filters | null;
  value: FilterValue;
  onChange: (key: keyof FilterValue, values: string[]) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const activeCount = (Object.keys(value) as (keyof FilterValue)[]).reduce((n, k) => n + value[k].length, 0);
  const cats = value.categories;
  const noCat = cats.length === 0;
  const showMonster = noCat || cats.includes('Monster');
  const showSpell = noCat || cats.includes('Spell');
  const showTrap = noCat || cats.includes('Trap');
  const showSkill = noCat || cats.includes('Skill');

  const setOptions: Option[] = useMemo(
    () => (filters?.sets ?? []).map(s => ({ value: s.code, label: `${s.name} (${s.code})` })),
    [filters],
  );

  return (
    <div className="filterbar" ref={ref}>
      <button type="button" className={`filter-toggle${activeCount > 0 ? ' has-active' : ''}`} onClick={() => setOpen(o => !o)}>
        <span aria-hidden="true">⚑</span> Filters{activeCount > 0 ? ` (${activeCount})` : ''}
      </button>
      {activeCount > 0 && (
        <button type="button" className="filter-clear" onClick={onClear}>Clear all</button>
      )}
      {open && (
        <div className="filter-panel">
          <MultiSelect label="Category" options={opts(filters?.categories ?? [])} selected={value.categories} onChange={v => onChange('categories', v)} />
          {showMonster && <>
            <MultiSelect label="Attribute" options={opts(filters?.attributes ?? [])} selected={value.attributes} onChange={v => onChange('attributes', v)} />
            <MultiSelect label="Monster Type" searchable options={opts(filters?.monsterTypes ?? [])} selected={value.monsterTypes} onChange={v => onChange('monsterTypes', v)} />
            <MultiSelect label="Card Type" options={opts(filters?.monsterCategories ?? [])} selected={value.monsterCategories} onChange={v => onChange('monsterCategories', v)} />
          </>}
          {showSpell && <MultiSelect label="Spell Type" options={opts(filters?.spellTypes ?? [])} selected={value.spellTypes} onChange={v => onChange('spellTypes', v)} />}
          {showTrap && <MultiSelect label="Trap Type" options={opts(filters?.trapTypes ?? [])} selected={value.trapTypes} onChange={v => onChange('trapTypes', v)} />}
          {showSkill && <MultiSelect label="Skill Character" searchable options={opts(filters?.skillCharacters ?? [])} selected={value.skillCharacters} onChange={v => onChange('skillCharacters', v)} />}
          <MultiSelect label="Set" searchable options={setOptions} selected={value.sets} onChange={v => onChange('sets', v)} />
          <button type="button" className="filter-done" onClick={() => setOpen(false)}>Done</button>
        </div>
      )}
    </div>
  );
}
